/**
 * Retro-arcade SFX for the PvP Battleship game, synthesized on the fly with the
 * Web Audio API (oscillator + gain envelopes). Nothing is loaded from disk, so
 * there is no asset pipeline: every sound is a short, distinctive tone/sweep
 * generated in code (mirrors the approach already used by
 * `inertia/lib/notification-sounds.ts`).
 *
 * A single shared `AudioContext` is created lazily and unlocked on the first
 * user gesture (browsers keep it `suspended` until then). When the context is
 * missing (SSR / unsupported), suspended, or the caller is muted, playback is a
 * silent no-op and never throws.
 *
 * The mute state (persisted in `localStorage`) is factored into the small pure
 * helpers at the bottom so it can be unit-tested without a DOM.
 */

/** The complete SFX vocabulary of the game. */
export type GameSoundName =
  | "shot"
  | "hit"
  | "miss"
  | "sunk"
  | "yourTurn"
  | "victory"
  | "defeat"
  | "deadlineTick";

/**
 * One scheduled oscillator segment. `slideTo`, when present, glides the
 * frequency from `frequency` to `slideTo` across the segment (a retro "sweep").
 */
type Tone = {
  frequency: number;
  start: number;
  duration: number;
  gain: number;
  type: OscillatorType;
  slideTo?: number;
};

/**
 * SFX definitions. Each is a tiny sequence of tones with a short attack and an
 * exponential decay, tuned to read as chip-tune arcade feedback.
 */
const SFX: Record<GameSoundName, Tone[]> = {
  // Launch: a fast high→low square sweep, like a shell leaving the cannon.
  shot: [{ frequency: 880, slideTo: 180, start: 0, duration: 0.14, gain: 0.16, type: "square" }],
  // Impact: a punchy low square burst.
  hit: [
    { frequency: 240, start: 0, duration: 0.09, gain: 0.2, type: "square" },
    { frequency: 150, start: 0.07, duration: 0.13, gain: 0.16, type: "square" },
  ],
  // Splash: a soft, dull low sine — the shot landed in the water.
  miss: [{ frequency: 200, slideTo: 130, start: 0, duration: 0.18, gain: 0.12, type: "sine" }],
  // Ship destroyed: a descending minor arpeggio with a saw edge (explosion).
  sunk: [
    { frequency: 440, start: 0, duration: 0.1, gain: 0.18, type: "sawtooth" },
    { frequency: 330, start: 0.09, duration: 0.1, gain: 0.16, type: "sawtooth" },
    { frequency: 208, start: 0.18, duration: 0.22, gain: 0.16, type: "sawtooth" },
  ],
  // Your move: a bright rising two-note attention chime.
  yourTurn: [
    { frequency: 660, start: 0, duration: 0.12, gain: 0.15, type: "triangle" },
    { frequency: 990, start: 0.1, duration: 0.2, gain: 0.14, type: "triangle" },
  ],
  // Win: a triumphant rising major arpeggio.
  victory: [
    { frequency: 523.25, start: 0, duration: 0.12, gain: 0.16, type: "triangle" },
    { frequency: 659.25, start: 0.11, duration: 0.12, gain: 0.16, type: "triangle" },
    { frequency: 783.99, start: 0.22, duration: 0.12, gain: 0.16, type: "triangle" },
    { frequency: 1046.5, start: 0.33, duration: 0.3, gain: 0.16, type: "triangle" },
  ],
  // Loss: a somber descending arpeggio.
  defeat: [
    { frequency: 523.25, start: 0, duration: 0.16, gain: 0.15, type: "sine" },
    { frequency: 392, start: 0.16, duration: 0.16, gain: 0.14, type: "sine" },
    { frequency: 261.63, start: 0.32, duration: 0.34, gain: 0.14, type: "sine" },
  ],
  // Countdown: a short, dry high tick.
  deadlineTick: [{ frequency: 1200, start: 0, duration: 0.05, gain: 0.12, type: "square" }],
};

let audioContext: AudioContext | null = null;

/**
 * Lazily create the single shared `AudioContext`. Returns `null` in any
 * environment without Web Audio (SSR, ancient browsers), so every caller can
 * degrade to a silent no-op. The `webkitAudioContext` global is declared by
 * `inertia/lib/notification-sounds.ts`.
 */
function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") {
    return null;
  }
  if (audioContext) {
    return audioContext;
  }
  const AudioContextClass = window.AudioContext ?? window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }
  try {
    audioContext = new AudioContextClass();
  } catch {
    return null;
  }
  return audioContext;
}

/**
 * Resume the shared context after a user gesture. Browsers suspend the context
 * until a gesture, so this must be called from within one (pointerdown/keydown)
 * for later `playGameSound` calls to be audible. Safe to call repeatedly.
 */
export function unlockGameAudio(): void {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    void ctx.resume();
  }
}

function scheduleTone(ctx: AudioContext, tone: Tone): void {
  const now = ctx.currentTime + tone.start;
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.type = tone.type;
  oscillator.frequency.setValueAtTime(tone.frequency, now);
  if (tone.slideTo !== undefined) {
    // A linear glide reads as a "sweep"; guard against 0 which is invalid for
    // the exponential variant, so linear is the safe choice here.
    oscillator.frequency.linearRampToValueAtTime(tone.slideTo, now + tone.duration);
  }

  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(tone.gain, now + 0.015);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + tone.duration);

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start(now);
  oscillator.stop(now + tone.duration + 0.03);
}

/**
 * Play one SFX. A no-op (never throws) when Web Audio is unavailable or the
 * context is still suspended (not yet unlocked by a gesture). Mute is enforced
 * by the caller ({@link useGameSounds}) before reaching here.
 */
export function playGameSound(name: GameSoundName): void {
  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }
  if (ctx.state === "suspended") {
    // Best-effort resume; if it is still suspended synchronously the sound is
    // simply skipped (the gesture-unlock path is the reliable one).
    void ctx.resume();
    if (ctx.state === "suspended") {
      return;
    }
  }

  for (const tone of SFX[name]) {
    scheduleTone(ctx, tone);
  }
}

// --- Pure mute-state helpers (unit-testable without a DOM) -------------------

/** Stable `localStorage` key for the arcade mute preference. */
export const MUTED_STORAGE_KEY = "arcade:muted";

/** Minimal storage surface, so the helpers can be tested with a fake store. */
export type StorageLike = Pick<Storage, "getItem" | "setItem">;

/**
 * Read the persisted mute preference. Defaults to unmuted (`false`) when there
 * is no storage or nothing was stored; never throws (private-mode storage can
 * throw on access).
 */
export function readStoredMuted(storage: StorageLike | null | undefined): boolean {
  if (!storage) {
    return false;
  }
  try {
    return storage.getItem(MUTED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persist the mute preference. Silently ignores storage failures. */
export function persistMuted(storage: StorageLike | null | undefined, muted: boolean): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(MUTED_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    // @expected-error-boundary: persisting a UI preference must never break
    // playback; a full/blocked store just means the toggle is not remembered.
  }
}
